import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiCheckCircle, FiClock, FiPlus, FiDownload } from 'react-icons/fi';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { usePermissions } from '../../hooks/usePermissions';

const TaskManager = () => {
  const { hasPermission } = usePermissions();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState({ from: '', to: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchTasks();
  }, [filter, dateFilter]);

  const fetchTasks = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.append('status', filter);
      if (dateFilter.from) params.append('dateFrom', dateFilter.from);
      if (dateFilter.to) params.append('dateTo', dateFilter.to);
      const { data } = await axios.get(`/api/crm/tasks?${params}`);
      setTasks(data.tasks || []);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const exportData = tasks.map(task => ({
      'Task Title': task.taskTitle,
      'Type': task.taskType,
      'Priority': task.priority,
      'Status': task.status,
      'Lead': task.lead?.name || '-',
      'Description': task.description || '-',
      'Due Date': task.dueDate ? new Date(task.dueDate).toLocaleString() : '-',
      'Created Date': new Date(task.createdAt).toLocaleString()
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Tasks');
    XLSX.writeFile(wb, `tasks-${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Exported successfully');
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`/api/crm/tasks/${taskId}`, { status });
      toast.success('Task status updated');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`/api/crm/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const statusColors = {
    Pending: 'bg-yellow-100 text-yellow-800',
    'In Progress': 'bg-blue-100 text-blue-800',
    Completed: 'bg-green-100 text-green-800',
    Cancelled: 'bg-red-100 text-red-800'
  };

  if (loading) return <div className="p-6">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/crm')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            <FiArrowLeft size={20} />
            Back to CRM Dashboard
          </button>
          <h1 className="text-2xl font-bold">My Tasks</h1>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportToExcel}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
          >
            <FiDownload size={16} />
            Export
          </button>
          <button
            onClick={() => navigate('/crm/tasks/new')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 flex items-center gap-2"
          >
            <FiPlus size={16} />
            New Task
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-4 p-4">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            {['all', 'Pending', 'In Progress', 'Completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1 rounded text-sm ${
                  filter === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {status === 'all' ? 'All Tasks' : status}
              </button>
            ))}
          </div>
          <div className="flex gap-4 items-center">
            <label className="text-sm font-medium text-gray-700">Date Range:</label>
            <input
              type="date"
              value={dateFilter.from}
              onChange={(e) => setDateFilter({ ...dateFilter, from: e.target.value })}
              className="border rounded px-3 py-1 text-sm"
              placeholder="From"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={dateFilter.to}
              onChange={(e) => setDateFilter({ ...dateFilter, to: e.target.value })}
              className="border rounded px-3 py-1 text-sm"
              placeholder="To"
            />
            {(dateFilter.from || dateFilter.to) && (
              <button
                onClick={() => setDateFilter({ from: '', to: '' })}
                className="text-sm text-blue-600 hover:underline"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {tasks.map((task) => (
              <tr key={task.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateTaskStatus(task.id, task.status === 'Completed' ? 'Pending' : 'Completed')}
                      className={`p-1 rounded ${
                        task.status === 'Completed' ? 'text-green-600' : 'text-gray-400 hover:text-green-600'
                      }`}
                    >
                      <FiCheckCircle size={18} />
                    </button>
                    <span className="font-medium text-sm">{task.taskTitle}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="px-2 py-1 bg-gray-100 rounded text-xs">{task.taskType}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium ${
                    task.priority === 'High' || task.priority === 'Urgent' ? 'text-red-600' :
                    task.priority === 'Medium' ? 'text-yellow-600' : 'text-green-600'
                  }`}>{task.priority}</span>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={task.status}
                    onChange={(e) => updateTaskStatus(task.id, e.target.value)}
                    className={`px-2 py-1 rounded text-xs font-medium border-0 cursor-pointer ${statusColors[task.status]}`}
                  >
                    <option value="Pending">Pending</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </td>
                <td className="px-4 py-3 text-sm">{task.lead?.name || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">{task.description || '-'}</td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {task.dueDate ? new Date(task.dueDate).toLocaleString() : '-'}
                </td>
                <td className="px-4 py-3 text-xs text-gray-600">
                  {new Date(task.createdAt).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {hasPermission('crm.update') && (
                    <button
                      onClick={() => navigate(`/crm/tasks/${task.id}/edit`)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Edit"
                    >
                      ✏️
                    </button>
                    )}
                    {hasPermission('crm.delete') && (
                    <button
                      onClick={() => deleteTask(task.id)}
                      className="text-red-600 hover:text-red-800"
                      title="Delete"
                    >
                      🗑️
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tasks.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <FiCheckCircle size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No tasks found. Create your first task!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskManager;